import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OrderCarrierRequestsDialog } from '#/features/carrier-requests'
import { PaymentEditForm } from '#/features/payment-edit'
import type { PaymentFormData } from '#/features/payment-edit'
import { GetActiveLoadboardsApiV1LoadboardsActiveGet200Item as ActiveLoadboard } from '#/shared/api/generated/loadboard/models'
import { useGetActiveLoadboardsApiV1LoadboardsActiveGet } from '#/shared/api/generated/loadboard/theCargoLoadboard'
import {
  LoadboardChoice,
  PostAction,
  ShipmentDispatchBriefDispatchType,
} from '#/shared/api/generated/shipment/models'
import type {
  ShipmentDetail,
  ShipmentDispatchBrief,
} from '#/shared/api/generated/shipment/models'
import { formatRelativeTime } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '#/shared/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '#/shared/ui/dropdown-menu'
import {
  ChevronRightIcon,
  MoreVerticalIcon,
  RefreshIcon,
  UploadIcon,
} from '#/shared/ui/icons'
import { Skeleton } from '#/shared/ui/skeleton'
import { usePostToLoadboard } from '#/widgets/detail-toolbar'
import {
  moneyToFormString,
  paymentToFormDefaults,
} from '#/widgets/payment-card'

import {
  isCdOrderNotSignedStatus,
  isDispatchedStatus,
  isSdOrderEditableStatus,
} from '#/entities/shipment'

import { useCancelDispatch } from '../model/use-cancel-dispatch'
import { useCancelOffer } from '../model/use-cancel-offer'
import { useUpdateSdOrder } from '../model/use-update-sd-order'
import { useShipmentSectionSavers } from '../model/use-shipment-section-edit'
import { LoadboardPaymentSummary } from './loadboard-payment-summary'
import { SidebarSection } from './sidebar-section'

type Board =
  | typeof ShipmentDispatchBriefDispatchType.central
  | typeof ShipmentDispatchBriefDispatchType.super

const BOARD_ORDER: Board[] = [
  ShipmentDispatchBriefDispatchType.central,
  ShipmentDispatchBriefDispatchType.super,
]

const BOARD_DOT: Record<Board, string> = {
  [ShipmentDispatchBriefDispatchType.central]: 'bg-blue-500',
  [ShipmentDispatchBriefDispatchType.super]: 'bg-orange-500',
}

const BOARD_NAME_KEY: Record<Board, string> = {
  [ShipmentDispatchBriefDispatchType.central]:
    'detailSidebar.loadboard.central',
  [ShipmentDispatchBriefDispatchType.super]: 'detailSidebar.loadboard.super',
}

const BOARD_POST_KEY: Record<Board, string> = {
  [ShipmentDispatchBriefDispatchType.central]: 'detailToolbar.postOptions.cd',
  [ShipmentDispatchBriefDispatchType.super]: 'detailToolbar.postOptions.slb',
}

// Loadboard row action buttons — 28px tall, 8px radius per Figma (12169-160258);
// the shared Button `xs` size is 32px/6px, so override height + radius here.
const ROW_ACTION_CLASS = 'h-7 rounded-lg'

function activeBoards(items: ActiveLoadboard[] | undefined): Board[] {
  const set = new Set<Board>()
  for (const item of items ?? []) {
    if (item === ActiveLoadboard.both) {
      set.add(ShipmentDispatchBriefDispatchType.central)
      set.add(ShipmentDispatchBriefDispatchType.super)
    } else {
      set.add(item)
    }
  }
  return BOARD_ORDER.filter((board) => set.has(board))
}

function boardDispatch(
  shipment: ShipmentDetail,
  board: Board,
): ShipmentDispatchBrief | undefined {
  return (shipment.dispatches ?? []).find(
    (dispatch) => dispatch.dispatch_type === board,
  )
}

type BoardState = 'none' | 'posted' | 'dispatched'

/** Actions wired to one loadboard mutation, so only the clicked button loads. */
type BoardActionType =
  | 'post'
  | 'repost'
  | 'remove'
  | 'cancel'
  | 'cancelOffer'
  | 'updateOrder'

const POST_ACTION_BY_TYPE: Record<'post' | 'repost' | 'remove', PostAction> = {
  post: PostAction.post,
  repost: PostAction.repost,
  remove: PostAction.delete,
}

interface BoardActions {
  post: (board: Board) => void
  repost: (board: Board) => void
  remove: (board: Board) => void
  removeAll: () => void
  cancel: (board: Board) => void
  cancelOffer: (board: Board) => void
  updateOrder: (board: Board) => void
  isLoading: (action: BoardActionType, board: Board) => boolean
  isRemovingAll: boolean
}

function useBoardActions(shipment: ShipmentDetail): BoardActions {
  const lb = usePostToLoadboard(shipment.id)
  const cancelDispatch = useCancelDispatch(shipment.id)
  const offer = useCancelOffer(shipment.id)
  const update = useUpdateSdOrder(shipment.id)
  // The board a shipment-level action (cancel order / cancel offer / update
  // order) was triggered from. The mutations are shipment-scoped, but the button
  // that spins must not be — only the triggering row's button shows loading.
  const [actionBoard, setActionBoard] = useState<Board | null>(null)

  const isLoading = (action: BoardActionType, board: Board) => {
    if (action === 'updateOrder')
      return update.isPending && actionBoard === board
    if (action === 'cancelOffer')
      return offer.isPending && actionBoard === board
    if (action === 'cancel')
      return cancelDispatch.isPending && actionBoard === board
    if (!lb.isPending || lb.pendingLoadboards !== board) return false
    return lb.pendingAction === POST_ACTION_BY_TYPE[action]
  }

  return {
    post: (board) => void lb.post(board),
    repost: (board) => void lb.repost(board),
    remove: (board) => void lb.remove(board),
    removeAll: () => void lb.remove(LoadboardChoice.both),
    cancel: (board) => {
      setActionBoard(board)
      cancelDispatch.cancel()
    },
    cancelOffer: (board) => {
      setActionBoard(board)
      offer.cancelOffer()
    },
    updateOrder: (board) => {
      setActionBoard(board)
      update.updateOrder()
    },
    isLoading,
    isRemovingAll:
      lb.isPending &&
      lb.pendingAction === PostAction.delete &&
      lb.pendingLoadboards === LoadboardChoice.both,
  }
}

function boardSubtitleKey(state: BoardState): string {
  if (state === 'dispatched') return 'detailSidebar.loadboard.dispatchedAgo'
  if (state === 'posted') return 'detailSidebar.loadboard.postedAgo'
  return 'detailSidebar.loadboard.notPosted'
}

/** Carrier-requests button + dialog on the dispatched Super Dispatch row.
 * While the load is only posted, Requests lives in the detail-toolbar header
 * (ShipmentRequestsSlot) instead. */
function RequestsButton({ shipmentId }: { shipmentId: string }) {
  const { t } = useTranslation()
  const [requestsOpen, setRequestsOpen] = useState(false)
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="xs"
        pressEffect
        onClick={() => setRequestsOpen(true)}
        className={ROW_ACTION_CLASS}
      >
        {t('detailToolbar.requests')}
        <ChevronRightIcon className="size-3.5" />
      </Button>
      <OrderCarrierRequestsDialog
        shipmentId={shipmentId}
        open={requestsOpen}
        onOpenChange={setRequestsOpen}
      />
    </>
  )
}

function RepostButton({
  board,
  actions,
}: {
  board: Board
  actions: BoardActions
}) {
  const { t } = useTranslation()
  const reposting = actions.isLoading('repost', board)
  return (
    <Button
      type="button"
      variant="outline"
      size="xs"
      pressEffect
      disabled={reposting}
      onClick={() => actions.repost(board)}
      className={ROW_ACTION_CLASS}
    >
      <RefreshIcon className={cn('size-3.5', reposting && 'animate-spin')} />
      {t('detailToolbar.repost')}
    </Button>
  )
}

function PostedActions({
  board,
  actions,
}: {
  board: Board
  actions: BoardActions
}) {
  const { t } = useTranslation()
  return (
    <>
      <RepostButton board={board} actions={actions} />
      <Button
        type="button"
        variant="outline"
        size="xs"
        pressEffect
        loading={actions.isLoading('remove', board)}
        onClick={() => actions.remove(board)}
        className={ROW_ACTION_CLASS}
      >
        {t('detailSidebar.loadboard.remove')}
      </Button>
    </>
  )
}

/** The SD kebab actions that need a heads-up before firing: Update Order pushes
 * whatever the shipment currently holds onto the live SD order (edit the
 * shipment first, or the previously posted info is re-sent unchanged); Cancel
 * Offer withdraws the accepted carrier's offer. */
type SdConfirmAction = 'updateOrder' | 'cancelOffer'

const SD_CONFIRM_KEYS: Record<
  SdConfirmAction,
  { title: string; description: string; cancel: string; confirm: string }
> = {
  updateOrder: {
    title: 'detailSidebar.loadboard.updateOrderConfirm.title',
    description: 'detailSidebar.loadboard.updateOrderConfirm.description',
    cancel: 'detailSidebar.loadboard.updateOrderConfirm.cancel',
    confirm: 'detailSidebar.loadboard.updateOrderConfirm.confirm',
  },
  cancelOffer: {
    title: 'detailSidebar.loadboard.cancelOfferConfirm.title',
    description: 'detailSidebar.loadboard.cancelOfferConfirm.description',
    cancel: 'detailSidebar.loadboard.cancelOfferConfirm.cancel',
    confirm: 'detailSidebar.loadboard.cancelOfferConfirm.confirm',
  },
}

function SdActionConfirmDialog({
  action,
  onCancel,
  onConfirm,
}: {
  action: SdConfirmAction | null
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation()
  const keys = SD_CONFIRM_KEYS[action ?? 'updateOrder']
  return (
    <Dialog
      open={action !== null}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel()
      }}
    >
      <DialogContent className="max-w-sm">
        <div className="flex flex-col gap-4 p-5">
          <div className="flex flex-col gap-1.5">
            <DialogTitle>{t(keys.title)}</DialogTitle>
            <DialogDescription>{t(keys.description)}</DialogDescription>
          </div>
          <div className="flex items-center justify-end gap-2.5">
            <Button
              type="button"
              variant="outline"
              size="xs"
              pressEffect
              className="px-3.5"
              onClick={onCancel}
            >
              {t(keys.cancel)}
            </Button>
            <Button
              type="button"
              size="xs"
              pressEffect
              className="px-3.5"
              onClick={onConfirm}
            >
              {t(keys.confirm)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Kebab menu on the Super Dispatch card while the order is live and in flight
 * (pre-signature through picked up): Update Order merge-patches the shipment
 * data onto the live SD order
 * via POST /sd/update-order (not a re-list), Cancel Offer cancels the accepted
 * carrier request (the SD offer), not the dispatch. */
function SdOrderMenu({
  board,
  actions,
}: {
  board: Board
  actions: BoardActions
}) {
  const { t } = useTranslation()
  const [confirmAction, setConfirmAction] = useState<SdConfirmAction | null>(
    null,
  )
  const busy =
    actions.isLoading('updateOrder', board) ||
    actions.isLoading('cancelOffer', board)
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="xs"
            pressEffect
            loading={busy}
            aria-label={t('detailSidebar.loadboard.orderActions')}
            className="size-7 rounded-lg px-0"
          >
            <MoreVerticalIcon className="size-4.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setConfirmAction('updateOrder')}>
            {t('detailSidebar.loadboard.updateOrder')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setConfirmAction('cancelOffer')}>
            {t('detailSidebar.loadboard.cancelOffer')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <SdActionConfirmDialog
        action={confirmAction}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => {
          if (confirmAction === 'updateOrder') actions.updateOrder(board)
          if (confirmAction === 'cancelOffer') actions.cancelOffer(board)
          setConfirmAction(null)
        }}
      />
    </>
  )
}

function DispatchedActions({
  board,
  shipment,
  actions,
}: {
  board: Board
  shipment: ShipmentDetail
  actions: BoardActions
}) {
  const { t } = useTranslation()
  const isSuper = board === ShipmentDispatchBriefDispatchType.super
  const sdOrderEditable = isSuper && isSdOrderEditableStatus(shipment.status)
  // CD pre-contract window: the listing can still be reposted to find another
  // carrier; Remove stays posted-only.
  const cdNotSigned = !isSuper && isCdOrderNotSignedStatus(shipment.status)
  return (
    <>
      {isSuper && <RequestsButton shipmentId={shipment.id} />}
      {cdNotSigned && <RepostButton board={board} actions={actions} />}
      {sdOrderEditable ? (
        <SdOrderMenu board={board} actions={actions} />
      ) : (
        <Button
          type="button"
          variant="outline"
          size="xs"
          pressEffect
          loading={actions.isLoading('cancel', board)}
          onClick={() => actions.cancel(board)}
          className={ROW_ACTION_CLASS}
        >
          {t('detailSidebar.loadboard.cancelOrder')}
        </Button>
      )}
    </>
  )
}

function BoardCardActions({
  state,
  board,
  shipment,
  actions,
}: {
  state: BoardState
  board: Board
  shipment: ShipmentDetail
  actions: BoardActions
}) {
  const { t } = useTranslation()
  if (state === 'dispatched') {
    return (
      <DispatchedActions board={board} shipment={shipment} actions={actions} />
    )
  }
  if (state === 'posted')
    return <PostedActions board={board} actions={actions} />
  return (
    <Button
      type="button"
      variant="outline"
      size="xs"
      pressEffect
      loading={actions.isLoading('post', board)}
      onClick={() => actions.post(board)}
      className={ROW_ACTION_CLASS}
    >
      <UploadIcon className="size-4" />
      {t(BOARD_POST_KEY[board])}
    </Button>
  )
}

function BoardCard({
  board,
  shipment,
  dispatch,
  actions,
}: {
  board: Board
  shipment: ShipmentDetail
  dispatch: ShipmentDispatchBrief | undefined
  actions: BoardActions
}) {
  const { t } = useTranslation()
  const state: BoardState = !dispatch
    ? 'none'
    : isDispatchedStatus(shipment.status)
      ? 'dispatched'
      : 'posted'
  return (
    <div className="flex items-center justify-between gap-2.5 rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          className={cn('size-2 shrink-0 rounded-full', BOARD_DOT[board])}
        />
        <div className="flex min-w-0 flex-col gap-0.75">
          <span className="truncate text-sm font-medium text-gray-900">
            {t(BOARD_NAME_KEY[board])}
          </span>
          <span className="text-xs text-gray-500">
            {dispatch
              ? t(boardSubtitleKey(state), {
                  time: formatRelativeTime(dispatch.created_at),
                })
              : t('detailSidebar.loadboard.notPosted')}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <BoardCardActions
          state={state}
          board={board}
          shipment={shipment}
          actions={actions}
        />
      </div>
    </div>
  )
}

function LoadboardBody({
  isLoading,
  boards,
  shipment,
  actions,
}: {
  isLoading: boolean
  boards: Board[]
  shipment: ShipmentDetail
  actions: BoardActions
}) {
  const { t } = useTranslation()
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2.5">
        <Skeleton className="h-15 w-full rounded-lg" />
        <Skeleton className="h-15 w-full rounded-lg" />
      </div>
    )
  }
  if (boards.length === 0) {
    return (
      <p className="px-2.5 text-sm text-gray-500">
        {t('detailSidebar.loadboard.noneConnected')}
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-2.5">
      {boards.map((board) => (
        <BoardCard
          key={board}
          board={board}
          shipment={shipment}
          dispatch={boardDispatch(shipment, board)}
          actions={actions}
        />
      ))}
    </div>
  )
}

function LoadboardCards({ shipment }: { shipment: ShipmentDetail }) {
  const { t } = useTranslation()
  const activeQuery = useGetActiveLoadboardsApiV1LoadboardsActiveGet()
  const actions = useBoardActions(shipment)
  const boards = activeBoards(activeQuery.data?.data)

  // "Remove All" only applies while listed on more than one board and not yet
  // dispatched (a dispatched order uses Cancel Order instead). (DEV-663)
  const postedBoardCount = isDispatchedStatus(shipment.status)
    ? 0
    : boards.filter((board) => boardDispatch(shipment, board)).length

  const removeAll =
    postedBoardCount > 1 ? (
      <button
        type="button"
        disabled={actions.isRemovingAll}
        onClick={actions.removeAll}
        className="cursor-pointer text-sm font-medium text-red-500 hover:underline disabled:opacity-50"
      >
        {t('detailSidebar.loadboard.removeAll')}
      </button>
    ) : undefined

  return (
    <SidebarSection
      label={t('detailSidebar.loadboard.title')}
      action={removeAll}
    >
      <LoadboardBody
        isLoading={activeQuery.isLoading}
        boards={boards}
        shipment={shipment}
        actions={actions}
      />
    </SidebarSection>
  )
}

function LoadboardPayment({ shipment }: { shipment: ShipmentDetail }) {
  const [editing, setEditing] = useState(false)
  const savers = useShipmentSectionSavers(shipment)

  const handleSave = async (data: PaymentFormData) => {
    await savers.onPaymentSave(data)
    setEditing(false)
  }

  const handleApplyTariff = (rate: number) => {
    void savers.onPaymentSave({
      ...paymentToFormDefaults(shipment),
      totalTariff: moneyToFormString(rate),
    })
  }

  if (editing) {
    return (
      <PaymentEditForm
        shipmentId={shipment.id}
        defaultValues={paymentToFormDefaults(shipment)}
        showCarrierDetails
        headerVariant="section"
        className="border-0 bg-transparent pt-0 pb-0"
        contentClassName="px-0"
        actionsAtBottom
        onSubmit={handleSave}
        onCancel={() => setEditing(false)}
      />
    )
  }

  return (
    <LoadboardPaymentSummary
      shipment={shipment}
      onEdit={() => setEditing(true)}
      onApplyTariff={handleApplyTariff}
    />
  )
}

function LoadboardSkeleton() {
  return (
    <div className="flex flex-col gap-6 p-4">
      <Skeleton className="h-15 w-full rounded-lg" />
      <Skeleton className="h-15 w-full rounded-lg" />
      <Skeleton className="h-40 w-full rounded-lg" />
    </div>
  )
}

export function SidebarLoadboardTab({
  shipment,
}: {
  shipment: ShipmentDetail | undefined
}) {
  if (!shipment) return <LoadboardSkeleton />
  return (
    <div className="flex flex-col gap-6 p-4">
      <LoadboardCards shipment={shipment} />
      <LoadboardPayment shipment={shipment} />
    </div>
  )
}
